import * as React from 'react';
import {
  ParentalConsentMap,
  ParentalConsentList,
  BirthCertificateDocs,
  BirthCertificateDocList,
  IDocumentsList
} from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus } from '../../../../utilities/documents';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';

interface IProps {
  infoOnly: boolean;
  documents: ParentalConsentMap;
  birthCertificate: BirthCertificateDocs;
  warning?: React.ReactElement;
  onResume: (url: string) => void;
  onClick: (sponsorType: string| null, docType: string | null , docKey: string, infoOnly: boolean) => void;
}

const ParentalConsent = (props: IProps) => {
  const { documents: docs, birthCertificate, infoOnly } = props;
  let hasDocs = false;
  const docNodes: React.ReactNode[] = [];
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.FINANCE_DOC, offer.status);

  if (docs) {
    const visibleDocs = getVisible(docs, infoOnly) as ParentalConsentList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: ParentalConsentList, index: number) => {
          const { item, key: docKey } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                hideButton={true}
                reason={item.reason}
                key={`parental-consent-${docKey}`}
                id={`parental-consent-${docKey}`}
                status={Status.Rejected}
                label={`Parent consent to travel letter ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                key={`parental-consent-${docKey}`}
                id={`parental-consent-${docKey}`}
                status={item.isDocumentFixed ? Status.Complete : getDocStatus(item.status, infoOnly, isSectionLocked)}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please Confirm' : undefined}
                buttonLabel={infoOnly ? 'Please review' : undefined}
                label={`Parent consent to travel letter ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onClick(null, 'parentalConsent', docKey, infoOnly)}
                hideButton={isSectionLocked}
              />
            );
          }
        })
      );
    }
  }

  if (birthCertificate && birthCertificate.certificate) {
    const visibleDocs = getVisible(birthCertificate.certificate!, infoOnly) as BirthCertificateDocList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BirthCertificateDocList, index: number) => {
          const { key, item } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                hideButton={true}
                reason={item.reason}
                key={`rejected-birth-certificate-${key}`}
                status={Status.Rejected}
                label={`Birth Certificate ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                key={`birth-certificate-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'To do'}
                status={item.isDocumentFixed ? Status.Complete : getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`Birth Certificate ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onClick('birthCertificate', 'certificate', key, infoOnly)}
                hideButton={isSectionLocked}
              />
            );
          }
        })
      );
    }
  }

  if (birthCertificate && birthCertificate.translation) {
    const visibleDocs = getVisible(birthCertificate.translation, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                hideButton={true}
                reason={item.reason}
                key={`rejected-birth-certificate-translation-${key}`}
                status={Status.Rejected}
                label={`Birth Certificate Translation ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                key={`birth-certificate-translation-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`Birth Certificate Translation ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onClick('birthCertificate', 'translation', key, infoOnly)}
                hideButton={isSectionLocked}
              />
            );
          }
        })
      );
    }
  }

  const getParentalConsentWarnings = () => {
    let warnings = null;
    const visibleDocs = getVisible(docs, infoOnly) as ParentalConsentList[];
    if (visibleDocs.length === 0) {
      return warnings;
    }
    const lastVisibleDoc: ParentalConsentList = visibleDocs[visibleDocs.length - 1];
    const { item } = lastVisibleDoc;
    if (item.isDocumentFixed || (item.isSigned === undefined && item.isDated === undefined)) {
      return warnings;
    }
    if (item.isSigned === false && item.isDated === false) {
      warnings = (
        <>
          <p>Your letter must be signed by your parent or guardian</p>
          <p>Your letter must be dated</p>
        </>
      );
    } else if (item.isSigned === false) {
      warnings = <p>Your letter must be signed by your parent or guardian</p>;
    } else if (item.isDated === false) {
      warnings = <p>Your letter must be dated</p>;
    }
    return warnings;
  };

  if (!hasDocs) return null;

  return (
    <ChecklistSection
      label={`You indicated you will be under 18 when you arrive`}
      edit={() => {
        props.onResume('personal-info/step-1');
      }}
      warning={getParentalConsentWarnings()}
    >
      {docNodes}
    </ChecklistSection>
  );
};

export default ParentalConsent;
